import { format } from "date-fns";

import { InboundStatus, LotType } from "./constants";
import { generateLotNumber, lotTemplateUsesToken } from "./utils";

/**
 * Lot-number generation for inbound shipments. Lives in utils (not the
 * inbound resource) because placement regenerates lot numbers from several
 * surfaces — the manager inbound form, the crew Gate Queue and gate check-in
 * placement (utils/shipmentPlacement.js), and both track-spotting hooks — and
 * the shared paths must not drag the manager app's component modules in.
 */

export const getPerpetualDate = (placedAt) => {
  return format(placedAt ? new Date(placedAt) : new Date(), "MMdd");
};

/**
 * True when a carlot shipment is still carrying the "TBD" placeholder that
 * autogenLotNumber emits while a {railCarNumber} template has no car number.
 *
 * The placeholder is only ever meant to be temporary, but the paths that fill
 * the car number in later — track placement, and the driver gate check-in —
 * are not the path that regenerates the lot. Callers use this to tell a lot
 * that is genuinely waiting from one a yard set deliberately, so regenerating
 * cannot overwrite a real number.
 *
 * @param {object} record - a shipment carrying lotType, lotNumber and account
 */
export const isAwaitingCarlotNumber = (record) => {
  if (!record || record.lotType !== LotType.CARLOT) return false;
  if (record.lotNumberOverride) return false;
  if (!lotTemplateUsesToken(record.account?.carlotLotNumber, "railCarNumber")) {
    return false;
  }
  const placeholder = (record.account?.lotNumberPrefix ?? "") + "TBD";
  return String(record.lotNumber ?? "") === placeholder;
};

export const autogenLotNumber = (lotType, formData, account) => {
  // Prefer account-configured templates when available
  // Pass both formData and account so templates can reference nested fields
  const record = { ...formData, account };
  const prefix = account?.lotNumberPrefix ?? "";
  const withPrefix = (body) => (prefix || "") + (body || "");

  if (lotType === LotType.CARLOT) {
    // The car / trailer number is not known until the unit reaches the yard, so
    // a template built on {railCarNumber} cannot produce a real lot number yet.
    // Hold at TBD rather than emit a partial number from the tokens that do
    // resolve; handleRailCarNumberChange regenerates once the number is typed.
    const awaitingCarNumber =
      lotTemplateUsesToken(account?.carlotLotNumber, "railCarNumber") &&
      !String(formData?.railCarNumber ?? "").trim();

    if (account?.carlotLotNumber && !awaitingCarNumber) {
      const lot = generateLotNumber(account.carlotLotNumber, record);
      if (lot) return withPrefix(lot);
    }
    // TBD is a placeholder for a number that is coming, so it is only honest
    // while a template is genuinely waiting on the car number. An account with
    // no carlot template is never going to produce one, and stamping every
    // such shipment "TBD" groups unrelated lots under a single label in
    // inventory search, lists and reports — where the field used to stay blank.
    return awaitingCarNumber ? withPrefix("TBD") : "";
  } else if (lotType === LotType.PERPETUAL) {
    // Only generate a perpetual lot number once placed; otherwise TBD
    if (
      !formData.status ||
      formData.status === InboundStatus.ADVICE_PENDING ||
      formData.status === InboundStatus.IN_TRANSIT ||
      formData.status === InboundStatus.ARRIVED
    ) {
      return withPrefix("TBD");
    }
    if (
      formData.status === InboundStatus.PLACED ||
      formData.status === InboundStatus.SIT_PLACED
    ) {
      if (account?.fifoLotNumber) {
        const lot = generateLotNumber(account.fifoLotNumber, record);
        if (lot) return withPrefix(lot);
      }
      // Legacy fallback
      return withPrefix(getPerpetualDate(formData.placedDateTime));
    }
    return "";
  }
  return "";
};
